import FiltersService from "@/services/FiltersService";
import { defineStore } from "pinia";
import { useAuthStore } from "@/stores/auth.store";
import { useNotificationStore } from "@/stores/notification.store";
import { i18n } from "@/main";

export const useArchivalFiltersStore = defineStore("archFilter", {
    state: () => ({
        /* ================= FILTERS ================= */
        filters: {
            dateTimeStart: new Date(
                new Date().setDate(new Date().getDate() - 1)
            ).toISOString(),
            dateTimeEnd: new Date().toISOString(),
            orderByColumnName1: "createdAt",
            orderDirection1: "desc",
            taggedOnly: false,
        },

        locations: [],
        laneNumbers: [],
        vehicleCategories: [2, 3, 5, 7, 8, 9, 10, 11, i18n.global.t("lack")],

        vehicleData: {
            plateLpr: "registration",
            vehicleMake: "vehicle-make",
            vehicleModel: "vehicle-model",
            similarity: "similarity",
            minSpeed: "speed",
            maxSpeed: "speed",
            minTotalLoad: "short-total-weight",
            maxTotalLoad: "short-total-weight",
            minVehLength: "length",
            maxVehLength: "length",
            minVehHeight: "height",
            maxVehHeight: "height",
            minNumberOfAxles: "no-of-axles",
            maxNumberOfAxles: "no-of-axles",
            minOverloadSA: "single-axle",
            maxOverloadSA: "single-axle",
            minOverloadMA: "multiple-axle",
            maxOverloadMA: "multiple-axle",
            minOverloadTL: "dmc",
            maxOverloadTL: "dmc",
            onBlackList: "blacklist-hit-only",
            recidivismOnly: "recidivism-only",
        },

        violations: {
            overload: i18n.global.t("permissible-load"),
            overheight: i18n.global.t("permissible-height"),
            overspeed: i18n.global.t("speed"),
            overlane: i18n.global.t("bypassing"),
            wrongDirection: i18n.global.t("wrong-way"),
        },

        orderByColumns: {
            createdAt: "measure-time",
            locationId: "location",
            plateLpr: "short-plate-no",
            speed: "speed",
            totalLoad: "total-weight",
            vehLength: "length",
            vehHeight: "height",
            numberOfAxles: "no-of-axles",
            ctg8p1: "vehicle-type",
            overloadSA: "single-axle",
            overloadMA: "multiple-axle",
            overloadTL: "dmc",
        },

        directionColumns: {
            desc: "ascending",
            asc: "descending",
        },

        /* ================= RESULTS CACHE ================= */
        savedLocations: [],
        savedFilters: {},
        savedVehicles: [],
        // Łączna liczba pomiarów dla zapamiętanych wyników (potrzebna do paginacji po powrocie
        // z widoku szczegółów pojazdu — bez tego licznik stron startuje od 0).
        savedTotalCount: 0,
        appliedFilters: {},

        /* ================= VALIDATION ================= */
        requireFields: [],
        error: null,

        authStore: useAuthStore(),
    }),

    getters: {
        items: (state) => state.filters,

        selectedLocation: (state) => {
            if (!state.filters.locations) return [];
            return state.locations.filter((l) =>
                state.filters.locations.includes(l.id)
            );
        },
    },

    actions: {
        /* ================= HELPERS ================= */

        hasCoreFilters() {
            const f = this.filters;
            return (
                Array.isArray(f.locations) &&
                f.locations.length > 0 &&
                !!f.dateTimeStart &&
                !!f.dateTimeEnd
            );
        },

        clearSavedResults() {
            this.savedVehicles = [];
            this.savedLocations = [];
            this.savedFilters = {};
            this.savedTotalCount = 0;
        },

        setAppliedFilters(filters) {
            this.appliedFilters = { ...filters };
        },

        clearAppliedFilters() {
            this.appliedFilters = {};
        },

        setSavedState(locations, filters, vehicles, totalCount = 0) {
            this.savedLocations = [...locations];
            this.savedFilters = { ...filters };
            this.savedVehicles = [...vehicles];
            this.savedTotalCount = Number(totalCount) || 0;
        },

        clearError() {
            this.error = null;
            this.requireFields = [];
        },

        /* ================= DATA ================= */

        async getLocations() {
            if (this.locations.length) return;

            try {
                const data = await FiltersService.getLocations();
                this.locations = data;

                const lanes = new Set();
                data.forEach((loc) => {
                    for (let i = 1; i <= loc.lanes.length; i++) {
                        lanes.add(i);
                    }
                });

                this.laneNumbers = [...lanes].sort((a, b) => a - b);
            } catch (e) {
                console.error("Błąd pobierania lokalizacji:", e);
            }
        },

        checkAllLocations() {
            const allSelected =
                Array.isArray(this.filters.locations) &&
                this.filters.locations.length === this.locations.length;

            this.filters.locations = allSelected
                ? []
                : this.locations.map((loc) => loc.id);
        },

        /* ================= FILTER OPS ================= */

        // Ustawia datę "do" na aktualną datę i godzinę.
        // Wywoływane przy powrocie na krok 1 archiwum, żeby zakres zawsze kończył się "teraz".
        refreshDateTimeEnd() {
            this.filters.dateTimeEnd = new Date().toISOString();
        },

        addItem(name, value) {
            switch (name) {
                case "LocationList": {
                    const arr = this.filters.locations ?? [];
                    const idx = arr.indexOf(value);

                    idx === -1 ? arr.push(value) : arr.splice(idx, 1);
                    arr.length
                        ? (this.filters.locations = arr)
                        : delete this.filters.locations;
                    break;
                }

                case "VehicleCategories": {
                    const key = this.vehicleCategories[value];
                    this.filters.ctg8p1 ??= [];
                    const i = this.filters.ctg8p1.indexOf(key);

                    i === -1
                        ? this.filters.ctg8p1.push(key)
                        : this.filters.ctg8p1.splice(i, 1);
                    break;
                }

                case "LaneNumbers": {
                    const key = this.laneNumbers[value];
                    this.filters.laneNumbers ??= [];
                    const i = this.filters.laneNumbers.indexOf(key);

                    i === -1
                        ? this.filters.laneNumbers.push(key)
                        : this.filters.laneNumbers.splice(i, 1);
                    break;
                }

                case "ViolationsAll": {
                    const allChecked = Object.keys(this.violations).every(
                        (k) => this.filters[k] !== undefined
                    );

                    Object.keys(this.violations).forEach((k) => {
                        allChecked
                            ? delete this.filters[k]
                            : (this.filters[k] = true);
                    });
                    break;
                }

                case "taggedOnly":
                    this.filters.taggedOnly = value;
                    break;

                default:
                    this.filters[name] = value;
            }

            this.clearSavedResults();
            this.clearAppliedFilters();
        },

        removeItem(name) {
            if (name === "rangeOfDate") {
                delete this.filters.dateTimeStart;
                delete this.filters.dateTimeEnd;
            } else if (name === "vehicleData") {
                Object.keys(this.vehicleData).forEach((k) => delete this.filters[k]);
            } else if (name === "violations") {
                Object.keys(this.violations).forEach((k) => delete this.filters[k]);
            } else {
                delete this.filters[name];
            }

            this.clearSavedResults();
            this.clearAppliedFilters();
        },

        /* ================= VALIDATION ================= */

        checkRequireData() {
            this.requireFields = [];
            this.error = null;

            if (!this.filters.dateTimeStart)
                this.requireFields.push("dateTimeStart");
            if (!this.filters.dateTimeEnd)
                this.requireFields.push("dateTimeEnd");
            if (
                !Array.isArray(this.filters.locations) ||
                this.filters.locations.length === 0
            )
                this.requireFields.push("locations");

            if (this.requireFields.length) {
                this.error = true;
                this.setErrors(i18n.global.t("required-parameter-missing"));
            }
        },

        /* ================= ERRORS ================= */

        // Przekazuje błąd do globalnych powiadomień (`useNotificationStore`).
        // Wołane z ArchivalMeasurements przy pobieraniu pomiarów i eksporcie
        // PDF/CSV/XLSX — brak tej akcji kończył się TypeError
        // "filtersStore.setErrors is not a function", a komunikat o błędzie
        // nigdy nie docierał do użytkownika.
        setErrors(error) {
            useNotificationStore().error(error);
        },
    },
});
